import { Module, VuexModule } from "vuex-module-decorators";

import { VignetteRouteConfig, VignetteRoutes, VignetteVisibility } from "@/types";
import NotFound from "@/views/404.vue";
import Contact from "@/views/Contact.vue";
import Debug from "@/views/Debug.vue";
import FAQ from "@/views/FAQ.vue";
import Loading from "@/views/Loading.vue";
import LoginRedirect from "@/views/LoginRedirect.vue";
import AssignedUsers from "@/views/my-account/AssignedUsers.vue";
import PayWall from "@/views/PayWall.vue";
import Profile from "@/views/Profile.vue";
import Search from "@/views/Search.vue";
import Success from "@/views/sign-up/Success.vue";
import ThankYou from "@/views/sign-up/ThankYou.vue";
import Welcome from "@/views/Welcome.vue";

const Briefcase = (): Promise<typeof import("@/views/Briefcase.vue")> =>
  import(/* webpackChunkName: "async-modules" */ "@/views/Briefcase.vue");
const Home = (): Promise<typeof import("@/views/Home.vue")> =>
  import(/* webpackChunkName: "async-modules" */ "@/views/Home.vue");
const MyAccount = (): Promise<typeof import("@/views/my-account/MyAccount.vue")> =>
  import(/* webpackChunkName: "async-modules" */ "@/views/my-account/MyAccount.vue");
const FeaturedProfilesPage = (): Promise<typeof import("@/views/FeaturedProfilesPage.vue")> =>
  import(/* webpackChunkName: "async-modules" */ "@/views/FeaturedProfilesPage.vue");
const SignUp = (): Promise<typeof import("@/views/sign-up/SignUp.vue")> =>
  import(/* webpackChunkName: "async-modules" */ "@/views/sign-up/SignUp.vue");

/**
 * this.context available for normal VueX style access and operations.
 */
@Module({ namespaced: true, name: "routes" })
export class RoutesModule extends VuexModule {
  routes: VignetteRouteConfig[] = [
    {
      path: "/",
      name: VignetteRoutes.Welcome,
      component: Welcome,
      label: "Welcome",
      icon: "public",
      visibility: VignetteVisibility.PUBLIC_ONLY,
      excludedFromNavBar: false,
      sortOrder: 10,
    },
    {
      path: "/home",
      name: VignetteRoutes.Home,
      component: Home,
      label: "Home",
      icon: "home",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 20,
    },
    // {
    //   path: "/search",
    //   name: "Search",
    //   component: Search,
    //   // this generates a separate chunk (search.[hash].js) for this route
    //   // which is lazy-loaded when the route is visited.
    //   // import(/* webpackChunkName: "search" */ "@/views/Search.vue"),
    // },
    {
      path: "/search",
      name: VignetteRoutes.Search,
      label: "Search",
      icon: "search",
      component: Search,
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 30,
    },
    {
      path: "/featured-profiles",
      name: VignetteRoutes.FeaturedProfiles,
      label: "Featured Profiles",
      icon: "star",
      component: FeaturedProfilesPage,
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 35,
    },
    {
      path: "/profile",
      name: VignetteRoutes.ProfileLanding,
      label: "Profile",
      icon: "person",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 40,
      redirect: { name: VignetteRoutes.Search },
    },
    {
      path: "/profile/:profileId(\\d+):profileName([-/][a-zA-Z-]+)?",
      name: VignetteRoutes.Profile,
      component: Profile,
      label: "Profile",
      icon: "person",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 45,
    },
    {
      path: "/briefcase/folder/:folderId(\\d+)",
      name: VignetteRoutes.BriefcaseFolder,
      component: Briefcase,
      label: "Briefcase",
      icon: "work",
      visibility: VignetteVisibility.WEB_USER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 50,
    },
    {
      path: "/briefcase",
      name: VignetteRoutes.Briefcase,
      component: Briefcase,
      label: "Briefcase",
      icon: "work",
      visibility: VignetteVisibility.WEB_USER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 55,
    },
    {
      path: "/my-account/assigned-users",
      name: VignetteRoutes.MyAccount_AssignedUsers,
      component: AssignedUsers,
      label: "Assigned Users",
      icon: "settings",
      visibility: VignetteVisibility.WEB_USER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 60,
      // TODO: check v-if="sessionInfo.isVignetteUserManager"
      // beforeEnter: (to, from, next) => {
      // }
    },
    {
      path: "/my-account",
      name: VignetteRoutes.MyAccount,
      component: MyAccount,
      label: "My Account",
      icon: "settings",
      visibility: VignetteVisibility.WEB_USER_ONLY,
      excludedFromNavBar: false,
      sortOrder: 65,
    },
    {
      path: "/sign-up/thank-you",
      name: VignetteRoutes.SignUp_ThankYou,
      component: ThankYou,
      label: "Thank You",
      icon: "add",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: true,
      sortOrder: 70,
    },
    {
      path: "/sign-up/success",
      name: VignetteRoutes.SignUp_Success,
      component: Success,
      label: "Success",
      icon: "add",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: true,
      sortOrder: 71,
    },
    {
      path: "/sign-up/registration",
      name: VignetteRoutes.SignUpRegistration,
      component: SignUp,
      label: "Register",
      icon: "add",
      visibility: VignetteVisibility.PUBLIC_ONLY,
      excludedFromNavBar: true,
      sortOrder: 75,
    },
    {
      path: "/sign-up",
      name: VignetteRoutes.SignUp,
      component: SignUp,
      label: "Try Vignette",
      icon: "add",
      visibility: VignetteVisibility.PUBLIC_ONLY,
      excludedFromNavBar: false,
      sortOrder: 76,
    },
    {
      path: "/faq",
      name: VignetteRoutes.Faq,
      component: FAQ,
      label: "FAQ",
      icon: "contact_support",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: false,
      sortOrder: 85,
    },
    {
      path: "/contact",
      name: VignetteRoutes.Contact,
      component: Contact,
      label: "Contact",
      icon: "question_answer",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: false,
      sortOrder: 90,
    },
    {
      path: "/paywall",
      name: VignetteRoutes.PayWall,
      component: PayWall,
      label: "Pay Wall",
      icon: "add",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: true,
      sortOrder: 91,
    },
    {
      path: "/loading",
      name: VignetteRoutes.Loading,
      component: Loading,
      label: "Loading",
      icon: "hourglass_empty",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: true,
      sortOrder: 98,
    },
    {
      path: "*",
      name: VignetteRoutes.NotFound,
      component: NotFound,
      label: "Not Found",
      icon: "error",
      visibility: VignetteVisibility.ALL,
      excludedFromNavBar: true,
      sortOrder: 99,
    },
    {
      path: "/login-redirect",
      name: VignetteRoutes.LoginRedirect,
      component: LoginRedirect,
      label: "Login",
      icon: "login",
      visibility: VignetteVisibility.PUBLIC_ONLY,
      excludedFromNavBar: false,
      sortOrder: 100,
    },
    {
      path: "/debug",
      name: VignetteRoutes.Debug,
      component: Debug,
      label: "Debug",
      icon: "bug_report",
      visibility: VignetteVisibility.MEMBER_ONLY,
      excludedFromNavBar: true,
      sortOrder: 101,
    },
  ];

  //***** GETTERS *****//

  get getRoutes(): VignetteRouteConfig[] {
    return this.routes;
  }

  get findRouteByName() {
    return (routeName: string | null | undefined): VignetteRouteConfig | undefined => {
      if (routeName == null) {
        return undefined;
      } else {
        return this.routes.find((route) => route.name === routeName);
      }
    };
  }

  get getNavBarRoutes(): VignetteRouteConfig[] {
    return this.routes.filter((route) => !route.excludedFromNavBar);
  }

  // TODO: dedupe getPublicNavBarRoutes below
  get getPublicRoutes(): VignetteRouteConfig[] {
    return this.routes
      .filter(
        (route) => route.visibility === VignetteVisibility.PUBLIC_ONLY || route.visibility === VignetteVisibility.ALL
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get getPublicNavBarRoutes(): VignetteRouteConfig[] {
    return this.getNavBarRoutes
      .filter(
        (route) => route.visibility === VignetteVisibility.PUBLIC_ONLY || route.visibility === VignetteVisibility.ALL
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  // TODO: dedupe getMemberOnlyNavBarRoutes below
  get getMemberOnlyRoutes(): VignetteRouteConfig[] {
    return this.routes
      .filter((route) => route.visibility !== VignetteVisibility.PUBLIC_ONLY)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get getMemberOnlyNavBarRoutes(): VignetteRouteConfig[] {
    return this.getNavBarRoutes
      .filter((route) => route.visibility !== VignetteVisibility.PUBLIC_ONLY)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get getLapsedRoutes(): VignetteRouteConfig[] {
    return this.routes
      .filter(
        (route) =>
          route.name === VignetteRoutes.Welcome ||
          route.name === VignetteRoutes.MyAccount ||
          route.name === VignetteRoutes.Contact ||
          route.name === VignetteRoutes.Faq ||
          route.name === VignetteRoutes.NotFound ||
          route.name === VignetteRoutes.SignUp_ThankYou
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get getLapsedNavBarRoutes(): VignetteRouteConfig[] {
    return this.getNavBarRoutes
      .filter(
        (route) =>
          route.name === VignetteRoutes.Welcome ||
          route.name === VignetteRoutes.MyAccount ||
          route.name === VignetteRoutes.Contact ||
          route.name === VignetteRoutes.Faq ||
          route.name === VignetteRoutes.NotFound ||
          route.name === VignetteRoutes.PayWall ||
          route.name === VignetteRoutes.SignUp_ThankYou
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get getNjMemberNavBarRoutes(): VignetteRouteConfig[] {
    return this.getNavBarRoutes
      .filter(
        (route) =>
          route.name === VignetteRoutes.Welcome ||
          route.name === VignetteRoutes.SignUp ||
          route.name === VignetteRoutes.Contact ||
          route.name === VignetteRoutes.Faq ||
          route.name === VignetteRoutes.NotFound ||
          route.name === VignetteRoutes.SignUp_ThankYou
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get getIPUserOnlyRoutes(): VignetteRouteConfig[] {
    return this.routes
      .filter(
        (route) => route.visibility === VignetteVisibility.MEMBER_ONLY || route.visibility === VignetteVisibility.ALL
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  get getIPUserOnlyNavBarRoutes(): VignetteRouteConfig[] {
    return this.getNavBarRoutes
      .filter(
        (route) => route.visibility === VignetteVisibility.MEMBER_ONLY || route.visibility === VignetteVisibility.ALL
      )
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  //***** MUTATIONS - SYNCHRONOUS *****//

  // DEV REMINDERS:
  // Mutation functions MUST NOT be async functions.
  // Also do not define Mutation & Actions as arrow️ functions, since we need to rebind them at runtime.

  //***** ACTIONS - ASYNCHRONOUS *****//
}
